import React from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    LinearProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { ReadyListItem } from '../onboardingIntelligence.types';
import { useBulkPush } from '../hooks/useBulkPush';
import {
    CARD_SX,
    HEADING_NAVY,
    PRIMARY_BLUE,
    SECTION_LABEL_SX,
    TILE_ACCENT,
} from './onboardingIntelligence.styles';

interface Props {
    items: ReadyListItem[];
    onRefresh: () => void;
}

const ReadyForProductionColumn: React.FC<Props> = ({ items, onRefresh }) => {
    const { busy, progress, failures, run } = useBulkPush(onRefresh);

    const handleBulk = () =>
        run(
            items.map((i) => ({ id: i.id, companyName: i.companyName })),
            { onProgress: () => undefined }
        );

    return (
        <Card
            data-testid='onboarding-intelligence-ready-column'
            sx={{ ...CARD_SX, height: '100%', padding: '16px 20px' }}
        >
            <Typography
                variant='subtitle2'
                sx={{ ...SECTION_LABEL_SX, marginBottom: '12px' }}
            >
                Ready to Go into Production
            </Typography>
            {failures.length > 0 && (
                <Alert
                    severity='warning'
                    role='alert'
                    sx={{ marginBottom: '12px' }}
                    data-testid='onboarding-intelligence-ready-failures'
                >
                    {failures.length} could not be pushed:{' '}
                    {failures.map((f) => f.companyName).join(', ')}
                </Alert>
            )}
            <List dense disablePadding>
                {items.length === 0 ? (
                    <ListItem>
                        <ListItemText
                            primary='No suppliers ready for production yet.'
                            primaryTypographyProps={{
                                variant: 'body2',
                                sx: { color: 'rgba(0,0,0,0.6)' },
                            }}
                        />
                    </ListItem>
                ) : (
                    items.map((item) => (
                        <ListItem
                            key={item.id}
                            data-testid={`onboarding-intelligence-ready-row-${item.id}`}
                            sx={{
                                backgroundColor: '#E9F5EC',
                                marginBottom: '6px',
                                borderRadius: '6px',
                                paddingY: '8px',
                                paddingX: '12px',
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            <ListItemText
                                primary={item.companyName}
                                primaryTypographyProps={{
                                    variant: 'body2',
                                    sx: { color: HEADING_NAVY },
                                }}
                            />
                            <ListItemIcon sx={{ minWidth: 'auto', marginLeft: '8px' }}>
                                <CheckCircleOutlineIcon
                                    fontSize='small'
                                    sx={{ color: TILE_ACCENT.ready }}
                                />
                            </ListItemIcon>
                        </ListItem>
                    ))
                )}
            </List>
            {progress && (
                <Box
                    id='onboarding-intelligence-bulk-progress'
                    role='status'
                    aria-live='polite'
                    aria-busy={busy || undefined}
                    sx={{ marginTop: '12px' }}
                    data-testid='onboarding-intelligence-bulk-progress'
                >
                    <LinearProgress
                        variant='determinate'
                        value={(progress.done / Math.max(progress.total, 1)) * 100}
                    />
                    <Typography variant='caption'>
                        Pushing {progress.done} / {progress.total} suppliers…
                    </Typography>
                </Box>
            )}
            <Button
                variant='outlined'
                fullWidth
                sx={{
                    marginTop: '12px',
                    backgroundColor: '#EEF1FE',
                    color: PRIMARY_BLUE,
                    borderColor: PRIMARY_BLUE,
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                        backgroundColor: '#DDE5FB',
                        borderColor: PRIMARY_BLUE,
                    },
                }}
                disabled={items.length === 0 || busy}
                aria-busy={busy || undefined}
                aria-describedby={progress ? 'onboarding-intelligence-bulk-progress' : undefined}
                onClick={handleBulk}
                data-testid='onboarding-intelligence-bulk-push'
            >
                Push {items.length} Supplier{items.length === 1 ? '' : 's'} to Production
            </Button>
        </Card>
    );
};

export default ReadyForProductionColumn;
